import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../config/adminConfig';
import { getDb, waitForFirebase } from '../services/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, limit } from 'firebase/firestore';

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  createdAt: Date;
  lastLoginAt: Date | null;
  isOnline: boolean;
  photoURL: string | null;
  emailVerified: boolean;
  providerId: string;
  videoCount: number;
  checkpointCount: number;
}

interface UserStats {
  totalUsers: number;
  onlineUsers: number;
  recentUsers: number; // 近3日加入
  verifiedUsers: number;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    onlineUsers: 0,
    recentUsers: 0,
    verifiedUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'email' | 'displayName' | 'createdAt' | 'lastLoginAt' | 'videoCount'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: '',
    email: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 檢查是否為管理員
  const isAdmin = isAdminUser(user?.email, user?.name);

  // 添加調試信息
  console.log('🔍 UserManagement 組件狀態:', {
    user: !!user,
    userEmail: user?.email,
    userName: user?.name,
    isAdmin,
    loading
  });

  useEffect(() => {
    if (isAdmin) {
      console.log('✅ 管理員權限確認，開始載入數據...');
      fetchUserData();
      // 每分鐘自動刷新數據
      const interval = setInterval(fetchUserData, 60000);
      return () => clearInterval(interval);
    } else if (user !== undefined) {
      // 如果用戶狀態已確定但不是管理員
      console.log('❌ 用戶不是管理員或未登入');
    }
  }, [isAdmin, user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      // 查詢 users 集合
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      // 查詢 sharedVbids 來統計每個用戶的影片和檢查點數量
      const videosQuery = query(collection(db, 'sharedVbids'));
      const videosSnapshot = await getDocs(videosQuery);
      
      const userVideoStats = new Map<string, { videoCount: number; checkpointCount: number }>();
      videosSnapshot.forEach(doc => {
        const data = doc.data();
        const userId = data.userId;
        if (userId) {
          if (!userVideoStats.has(userId)) {
            userVideoStats.set(userId, { videoCount: 0, checkpointCount: 0 });
          }
          const stats = userVideoStats.get(userId)!;
          stats.videoCount++;
          stats.checkpointCount += (data.checkpointCount || 0);
        }
      });

      const now = new Date();
      const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
      
      const usersData: UserData[] = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        const stats = userVideoStats.get(doc.id) || { videoCount: 0, checkpointCount: 0 };
        
        return {
          uid: doc.id,
          email: data.email || '',
          displayName: data.displayName || data.name || '未設定',
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || null,
          isOnline: data.isOnline || false,
          photoURL: data.photoURL || null,
          emailVerified: data.emailVerified || false,
          providerId: data.providerId || 'unknown',
          videoCount: stats.videoCount,
          checkpointCount: stats.checkpointCount
        };
      });

      // 計算統計數據
      const stats: UserStats = {
        totalUsers: usersData.length,
        onlineUsers: usersData.filter(u => u.isOnline).length,
        recentUsers: usersData.filter(u => u.createdAt >= threeDaysAgo).length,
        verifiedUsers: usersData.filter(u => u.emailVerified).length
      };

      setUsers(usersData);
      setUserStats(stats);
      setError(null);
    } catch (error) {
      console.error('❌ 載入使用者數據失敗:', error);
      setError('載入使用者數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (sortBy) {
      case 'email':
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
        break;
      case 'displayName':
        aValue = a.displayName.toLowerCase();
        bValue = b.displayName.toLowerCase();
        break;
      case 'createdAt':
        aValue = a.createdAt.getTime();
        bValue = b.createdAt.getTime();
        break;
      case 'lastLoginAt':
        aValue = a.lastLoginAt?.getTime() || 0;
        bValue = b.lastLoginAt?.getTime() || 0;
        break;
      case 'videoCount':
        aValue = a.videoCount;
        bValue = b.videoCount;
        break;
      default:
        return 0;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleEditUser = (user: UserData) => {
    setSelectedUser(user);
    setEditForm({
      displayName: user.displayName,
      email: user.email
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    
    try {
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        displayName: editForm.displayName,
        email: editForm.email,
        updatedAt: new Date()
      });

      setSnackbar({ open: true, message: '使用者資料更新成功', severity: 'success' });
      setEditDialogOpen(false);
      fetchUserData();
    } catch (error) {
      console.error('❌ 更新使用者資料失敗:', error);
      setSnackbar({ open: true, message: '更新失敗，請稍後再試', severity: 'error' });
    }
  };

  const handleDeleteUser = (user: UserData) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      // 刪除用戶文檔
      await deleteDoc(doc(db, 'users', selectedUser.uid));
      
      setSnackbar({ open: true, message: '使用者已刪除', severity: 'success' });
      setDeleteDialogOpen(false);
      fetchUserData();
    } catch (error) {
      console.error('❌ 刪除使用者失敗:', error);
      setSnackbar({ open: true, message: '刪除失敗，請稍後再試', severity: 'error' });
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '從未登入';
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 如果用戶狀態還在加載中，顯示載入中
  if (user === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>檢查權限中...</Typography>
      </Box>
    );
  }

  // 如果用戶已載入但不是管理員
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          您沒有權限訪問此頁面
        </Alert>
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          當前用戶: {user?.email || user?.name || '未登入'}
        </Typography>
        <Typography sx={{ mt: 1, color: 'text.secondary' }}>
          管理員 Email: jeff@redball.com.tw
        </Typography>
      </Box>
    );
  }

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>載入使用者數據中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 頁面標題 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminIcon sx={{ fontSize: 32, color: '#d32f2f', mr: 1 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          使用者管理
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="刷新數據">
            <IconButton onClick={fetchUserData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 統計卡片 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {userStats.totalUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    總使用者數
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LoginIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {userStats.onlineUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    線上使用者
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ fontSize: 40, color: '#ff9800', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {userStats.recentUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    近3日加入
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ fontSize: 40, color: '#9c27b0', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {userStats.verifiedUsers}
                  </Typography>
                  <Typography color="text.secondary">
                    已驗證用戶
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 使用者列表 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'email'}
                    direction={sortBy === 'email' ? sortOrder : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    使用者ID/Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'displayName'}
                    direction={sortBy === 'displayName' ? sortOrder : 'asc'}
                    onClick={() => handleSort('displayName')}
                  >
                    顯示名稱
                  </TableSortLabel>
                </TableCell>
                <TableCell>登入狀態</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'createdAt'}
                    direction={sortBy === 'createdAt' ? sortOrder : 'asc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    註冊時間
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'lastLoginAt'}
                    direction={sortBy === 'lastLoginAt' ? sortOrder : 'asc'}
                    onClick={() => handleSort('lastLoginAt')}
                  >
                    最後登入
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'videoCount'}
                    direction={sortBy === 'videoCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('videoCount')}
                  >
                    影片數
                  </TableSortLabel>
                </TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.uid}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {user.uid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.photoURL && (
                        <img 
                          src={user.photoURL} 
                          alt="avatar" 
                          style={{ width: 24, height: 24, borderRadius: '50%' }}
                        />
                      )}
                      <Typography>{user.displayName}</Typography>
                      {user.emailVerified && (
                        <Chip label="已驗證" size="small" color="success" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isOnline ? '線上' : '離線'}
                      color={user.isOnline ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.lastLoginAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        影片: {user.videoCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        檢查點: {user.checkpointCount}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="編輯使用者">
                        <IconButton size="small" onClick={() => handleEditUser(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="刪除使用者">
                        <IconButton size="small" onClick={() => handleDeleteUser(user)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 編輯使用者對話框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>編輯使用者資料</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="顯示名稱"
            value={editForm.displayName}
            onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleSaveEdit} variant="contained">儲存</Button>
        </DialogActions>
      </Dialog>

      {/* 刪除使用者確認對話框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>確認刪除使用者</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除使用者 "{selectedUser?.displayName}" 嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功/錯誤訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default UserManagement;
